import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Organisation } from '../../../../apollo/queries';
import {
  AllCustomRoleResponse,
  CustomRoleResponse,
  GET_CUSTOM_ROLES_N_PERMISSIONS,
} from '../../../../apollo/queries/v3/RolesNPermissions/getAllCustomRolesNPermissions.queries';
import {
  GET_CUSTOM_ROLE_BY_ID,
  GetCustomRoleResponse,
} from '../../../../apollo/queries/v3/RolesNPermissions/getCustomRoleById.queries';
import {
  GET_PERMISSIONS_CATEGORIES,
  PermissionCategoryResponse,
  PermissionsCategoryResponseData,
} from '../../../../apollo/queries/v3/RolesNPermissions/permissionCategories.queries';
import {
  CREATE_NEW_ROLE,
  CreateNewRoleInput,
} from '../../../../apollo/queries/v3/RolesNPermissions/createNewRole.queries';
import {
  UPDATE_CUSTOM_ROLE,
  UpdateCustomRoleInput,
} from '../../../../apollo/queries/v3/RolesNPermissions/updateCustomRole.queries';
import { RNPToastHelper } from '../../RNPToaster';
import {
  buildBulkUploadRolesCsv,
  downloadCsvContent,
  getBulkUploadTemplateCsv,
  parseBulkUploadRolesCsv,
} from '../utils/customRolesCsv';
import {
  buildImportSummary,
  CustomRolesImportSummary,
  getImportErrorMessage,
  RoleImportResult,
} from '../utils/importCustomRolesSummary';
import { CsvOperationProgress } from '../CsvOperationProgressBar';
import { resolvePermissionDependencies } from '../../utils/permissionDependencies';
import {
  CUSTOM_ROLES_EXPORT_BUILDING_FILE,
  CUSTOM_ROLES_EXPORT_FETCHING_ROLES,
  CUSTOM_ROLES_EXPORT_PROCESSING_ROLE,
  CUSTOM_ROLES_IMPORT_LOADING_PERMISSIONS,
  CUSTOM_ROLES_IMPORT_PROCESSING_ROLE,
  CUSTOM_ROLES_IMPORT_READING_FILE,
  CUSTOM_ROLES_IMPORT_REFRESHING,
  IMPORT_ROLE_CREATED_MESSAGE,
  IMPORT_ROLE_MISSING_PERMISSIONS,
  IMPORT_ROLE_UPDATED_MESSAGE,
  ROLES_BULK_UPLOAD_EXPORT_FILENAME,
  ROLES_BULK_UPLOAD_TEMPLATE_FILENAME,
} from '../../../../utils/constants';

const ALL_ROLES_PAGE_SIZE = 100;
const EXPORT_FETCH_ROLES_WEIGHT = 25;
const EXPORT_ROLE_DETAILS_WEIGHT = 65;
const IMPORT_PREPARE_WEIGHT = 10;
const IMPORT_ROLES_WEIGHT = 80;

const formatProgressLabel = (
  template: string,
  values: Record<string, string | number>,
): string =>
  Object.entries(values).reduce(
    (label, [key, value]) => label.replace(`:${key}`, String(value)),
    template,
  );

type Props = {
  onImportComplete?: () => void;
};

const fetchAllCustomRoles = async (
  client: ReturnType<typeof useApolloClient>,
  onProgress?: (percent: number) => void,
): Promise<CustomRoleResponse[]> => {
  const roles: CustomRoleResponse[] = [];
  let pageNumber = 0;
  let totalCount = 0;

  do {
    const { data } = await client.query<AllCustomRoleResponse>({
      query: GET_CUSTOM_ROLES_N_PERMISSIONS,
      variables: {
        pageNumber,
        pageSize: ALL_ROLES_PAGE_SIZE,
        search: '',
      },
      fetchPolicy: 'network-only',
    });

    const pageRoles = data?.allCustomRoles?.roles || [];
    totalCount = data?.allCustomRoles?.totalCount || 0;
    roles.push(...pageRoles);
    pageNumber += 1;

    if (totalCount > 0) {
      onProgress?.(
        Math.round((roles.length / totalCount) * EXPORT_FETCH_ROLES_WEIGHT),
      );
    }
  } while (roles.length < totalCount);

  onProgress?.(EXPORT_FETCH_ROLES_WEIGHT);
  return roles;
};

const fetchPermissionCategories = async (
  client: ReturnType<typeof useApolloClient>,
): Promise<PermissionCategoryResponse[]> => {
  const { data } = await client.query<PermissionsCategoryResponseData>({
    query: GET_PERMISSIONS_CATEGORIES,
    fetchPolicy: 'network-only',
  });

  return data?.permissionCategories || [];
};

const importRole = async ({
  client,
  organisation,
  permissionCategories,
  role,
}: {
  client: ReturnType<typeof useApolloClient>;
  organisation: Pick<Organisation, 'name' | 'code'>;
  permissionCategories: PermissionCategoryResponse[];
  role: {
    roleUuid: string;
    roleName: string;
    description: string;
    permissions: string[];
  };
}): Promise<RoleImportResult> => {
  const baseResult = {
    roleName: role.roleName,
    roleUuid: role.roleUuid,
  };

  const permissions = resolvePermissionDependencies(
    role.permissions,
    permissionCategories,
  );
  const description = role.description || role.roleName;

  if (!permissions.length) {
    return {
      ...baseResult,
      status: 'failed',
      message: IMPORT_ROLE_MISSING_PERMISSIONS,
    };
  }

  try {
    if (role.roleUuid) {
      const input: UpdateCustomRoleInput = {
        id: role.roleUuid,
        name: role.roleName,
        description,
        permissions,
      };

      await client.mutate({
        mutation: UPDATE_CUSTOM_ROLE,
        variables: { input },
      });

      return {
        ...baseResult,
        status: 'updated',
        message: IMPORT_ROLE_UPDATED_MESSAGE,
      };
    }

    const input: CreateNewRoleInput = {
      name: role.roleName,
      description,
      permissions,
      organisation: {
        code: organisation.code,
        name: organisation.name,
      },
    };

    await client.mutate({
      mutation: CREATE_NEW_ROLE,
      variables: { input },
    });

    return {
      ...baseResult,
      status: 'created',
      message: IMPORT_ROLE_CREATED_MESSAGE,
    };
  } catch (error) {
    return {
      ...baseResult,
      status: 'failed',
      message: getImportErrorMessage(error),
    };
  }
};

const useCustomRolesCsv = ({ onImportComplete }: Props = {}) => {
  const client = useApolloClient();
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [exportProgress, setExportProgress] =
    useState<CsvOperationProgress | null>(null);
  const [importProgress, setImportProgress] =
    useState<CsvOperationProgress | null>(null);

  const downloadTemplate = useCallback(() => {
    downloadCsvContent(
      getBulkUploadTemplateCsv(),
      ROLES_BULK_UPLOAD_TEMPLATE_FILENAME,
    );
  }, []);

  const exportCustomRoles = useCallback(async () => {
    setIsExporting(true);
    setExportProgress({
      percent: 0,
      label: CUSTOM_ROLES_EXPORT_FETCHING_ROLES,
    });

    try {
      const roles = await fetchAllCustomRoles(client, (percent) => {
        setExportProgress({
          percent,
          label: CUSTOM_ROLES_EXPORT_FETCHING_ROLES,
        });
      });

      const rolesWithPermissions = [];

      for (let index = 0; index < roles.length; index += 1) {
        const role = roles[index];

        setExportProgress({
          percent:
            EXPORT_FETCH_ROLES_WEIGHT +
            Math.round(
              ((index + 1) / Math.max(roles.length, 1)) *
                EXPORT_ROLE_DETAILS_WEIGHT,
            ),
          label: formatProgressLabel(CUSTOM_ROLES_EXPORT_PROCESSING_ROLE, {
            current: index + 1,
            total: roles.length,
          }),
        });

        try {
          const { data } = await client.query<GetCustomRoleResponse>({
            query: GET_CUSTOM_ROLE_BY_ID,
            variables: { input: { id: role.id } },
            fetchPolicy: 'network-only',
          });

          const rolePermissions =
            data?.customRole?.permissionsToCategories.map(
              (item) => item.permission,
            ) || role.permissions;

          rolesWithPermissions.push({
            id: role.id,
            name: role.name,
            description:
              data?.customRole?.description || role.description || role.name,
            permissions: Array.from(new Set(rolePermissions)),
          });
        } catch {
          rolesWithPermissions.push({
            id: role.id,
            name: role.name,
            description: role.description || role.name,
            permissions: role.permissions,
          });
        }
      }

      setExportProgress({
        percent: EXPORT_FETCH_ROLES_WEIGHT + EXPORT_ROLE_DETAILS_WEIGHT,
        label: CUSTOM_ROLES_EXPORT_BUILDING_FILE,
      });

      const csvContent = buildBulkUploadRolesCsv(rolesWithPermissions);
      downloadCsvContent(csvContent, ROLES_BULK_UPLOAD_EXPORT_FILENAME);
      RNPToastHelper.exportCustomRolesSuccess();
    } catch {
      RNPToastHelper.exportCustomRolesError();
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  }, [client]);

  const importCustomRoles = useCallback(
    async (
      file: File,
      organisation: Pick<Organisation, 'name' | 'code'>,
    ): Promise<CustomRolesImportSummary | null> => {
      setIsImporting(true);
      setImportProgress({
        percent: 0,
        label: CUSTOM_ROLES_IMPORT_READING_FILE,
      });

      try {
        const csvText = await file.text();
        const { roles } = parseBulkUploadRolesCsv(csvText);

        setImportProgress({
          percent: 5,
          label: CUSTOM_ROLES_IMPORT_LOADING_PERMISSIONS,
        });

        const permissionCategories = await fetchPermissionCategories(client);
        const results: RoleImportResult[] = [];

        for (let index = 0; index < roles.length; index += 1) {
          const role = roles[index];

          setImportProgress({
            percent:
              IMPORT_PREPARE_WEIGHT +
              Math.round(
                ((index + 1) / Math.max(roles.length, 1)) * IMPORT_ROLES_WEIGHT,
              ),
            label: formatProgressLabel(CUSTOM_ROLES_IMPORT_PROCESSING_ROLE, {
              current: index + 1,
              total: roles.length,
            }),
          });

          const result = await importRole({
            client,
            organisation,
            permissionCategories,
            role,
          });
          results.push(result);
        }

        const summary = buildImportSummary(results);

        if (summary.created > 0 || summary.updated > 0) {
          setImportProgress({
            percent: IMPORT_PREPARE_WEIGHT + IMPORT_ROLES_WEIGHT,
            label: CUSTOM_ROLES_IMPORT_REFRESHING,
          });

          await client.refetchQueries({
            include: [GET_CUSTOM_ROLES_N_PERMISSIONS],
          });
          onImportComplete?.();
        }

        RNPToastHelper.importCustomRolesSummary(summary);
        return summary;
      } catch (error) {
        RNPToastHelper.importCustomRolesError(getImportErrorMessage(error));
        return null;
      } finally {
        setIsImporting(false);
        setImportProgress(null);
      }
    },
    [client, onImportComplete],
  );

  return {
    downloadTemplate,
    exportCustomRoles,
    importCustomRoles,
    isExporting,
    isImporting,
    exportProgress,
    importProgress,
  };
};

export default useCustomRolesCsv;
